import type { RefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import i18n from '@/i18n';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { GEOLOCATION_POLICY } from '../constants';
import type { CheckType, GeolocationPolicy, GeoStatus, SubmitBlockReason } from '../types';
import type { ClassifiedError } from '../utils/errors';
import { CameraPreview } from './CameraPreview';
import type { CameraPreviewHandle } from './CameraPreview';
import { MockMap } from './MockMap';
import { LocationStatusPill } from './LocationStatusPill';

interface PunchcardRegisterCardProps {
  now: Date;
  location: { lat: number; lng: number } | null;
  geoStatus: GeoStatus;
  geoPolicy: GeolocationPolicy;
  geoBlocked: boolean;
  geoAwaitingLocation: boolean;
  geoRetrying: boolean;
  timezoneDisplay: string;
  photoRequired: boolean;
  photoConsent: boolean;
  cameraRef: RefObject<CameraPreviewHandle | null>;
  type: CheckType;
  submitting: boolean;
  submitError: ClassifiedError | null;
  writeEnabled: boolean;
  onTypeChange: (type: CheckType) => void;
  onSubmit: () => void;
  onRetryLocation: () => void;
}

function getDateLocale(lang: string) {
  if (lang.startsWith('en')) return enUS;
  if (lang.startsWith('es')) return es;
  return ptBR;
}

function getSubmitLabel(type: CheckType, submitting: boolean): string {
  if (submitting) return 'Registrando…';
  return type === 'entrando' ? 'Confirmar Entrada' : 'Confirmar Saída';
}

interface SubmitBlockInputs {
  submitting: boolean;
  writeEnabled: boolean;
  geoBlocked: boolean;
  geoAwaitingLocation: boolean;
}

// `writeEnabled: false` (→ 'write-disabled') é inalcançável hoje via PunchcardCardPage: a
// página intercepta `activeAgreement && !config.writeEnabled` antes de montar este componente
// e renderiza sua própria tela "Registro não autorizado" (confirmado em navegador real — todo
// acordo que chega a este card tem writeEnabled: true). Mantido mesmo assim, testado, para o
// card continuar correto por si só se um dia for renderizado sem essa guarda da página; removê-lo
// exigiria também remover a prop `writeEnabled`, o que está fora deste escopo.
function getSubmitBlockReason({
  submitting,
  writeEnabled,
  geoBlocked,
  geoAwaitingLocation,
}: SubmitBlockInputs): SubmitBlockReason {
  if (submitting) return 'submitting';
  if (!writeEnabled) return 'write-disabled';
  if (geoBlocked) return 'geo-blocked';
  if (geoAwaitingLocation) return 'geo-loading';
  return null;
}

// Só os motivos sem mensagem em outro lugar do card: `submitting` já aparece no rótulo do
// botão, e `geo-blocked` já tem o alerta do LocationStatusPill.
const BLOCK_REASON_MESSAGES: Partial<Record<Exclude<SubmitBlockReason, null>, string>> = {
  'write-disabled': 'Registro desabilitado para este acordo.',
  'geo-loading': 'Aguardando confirmação da localização para liberar o registro.',
};

/**
 * Main punch-in/punch-out card: shows the current time, the geolocation status,
 * the camera or map preview, and the submit action. The submit button stays
 * disabled while blocked by geolocation or awaiting a location the agreement's
 * policy needs; geolocation feedback is hidden when the policy dispenses it.
 */
export function PunchcardRegisterCard({
  now,
  location,
  geoStatus,
  geoPolicy,
  geoBlocked,
  geoAwaitingLocation,
  geoRetrying,
  timezoneDisplay,
  photoRequired,
  photoConsent,
  cameraRef,
  type,
  submitting,
  submitError,
  writeEnabled,
  onTypeChange,
  onSubmit,
  onRetryLocation,
}: PunchcardRegisterCardProps) {
  const locale = getDateLocale(i18n.language);
  const submitBlockReason = getSubmitBlockReason({
    submitting,
    writeEnabled,
    geoBlocked,
    geoAwaitingLocation,
  });
  const submitDisabled = submitBlockReason !== null;
  const blockMessage = submitBlockReason ? BLOCK_REASON_MESSAGES[submitBlockReason] : undefined;
  const showCamera = photoRequired && photoConsent;
  const showGeo = geoPolicy !== GEOLOCATION_POLICY.DISPENSED;
  const showRetryLocation = showGeo && geoStatus !== 'loading' && geoStatus !== 'success';

  return (
    <div className="flex flex-col px-6 pb-4">
      <div className="mb-6 flex flex-col items-center mt-8">
        <p className="mb-2 text-sm font-medium uppercase tracking-widest text-slate-500">
          {format(now, 'EEEE, dd MMM', { locale })}
        </p>
        <h1
          className="tabular-nums text-6xl font-black tracking-tighter text-slate-900"
          aria-label="Hora atual"
        >
          {format(now, 'HH:mm', { locale })}
        </h1>
        {showGeo && (
          <LocationStatusPill
            location={location}
            status={geoStatus}
            contingencyAllowed={geoPolicy === GEOLOCATION_POLICY.FLEXIBLE}
            retrying={geoRetrying}
            className="mt-4"
          />
        )}
        {showRetryLocation && (
          <button
            type="button"
            onClick={onRetryLocation}
            className="mt-2 text-xs font-medium text-slate-500 underline underline-offset-2 hover:text-slate-700"
          >
            Tentar obter localização novamente
          </button>
        )}
      </div>

      {showCamera ? (
        <CameraPreview ref={cameraRef} />
      ) : (
        showGeo && <MockMap hasLocation={!!location} />
      )}

      <div className="mt-auto space-y-4">
        <div className="relative flex gap-1 rounded-xl bg-slate-200/70 dark:bg-background p-1" role="group" aria-label="Tipo de registro">
          {(['entrando', 'saindo'] as CheckType[]).map((opt) => (
            <button
              key={opt}
              onClick={() => onTypeChange(opt)}
              className={cn(
                'relative z-10 flex-1 rounded-lg py-2 text-sm font-semibold transition-all',
                type === opt
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'font-medium text-slate-500 hover:text-slate-700 dark:hover:text-foreground',
              )}
              aria-pressed={type === opt}
            >
              {opt === 'entrando' ? 'Entrada' : 'Saída'}
            </button>
          ))}
        </div>

        <Button
          size="xl"
          className="w-full gap-2 rounded-2xl shadow-lg"
          disabled={submitDisabled}
          onClick={onSubmit}
          aria-label={getSubmitLabel(type, submitting)}
        >
          {submitting ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : null}
          {getSubmitLabel(type, submitting)}
        </Button>

        {blockMessage && <p className="text-center text-xs text-slate-400">{blockMessage}</p>}

        {submitError && (
          <div
            role="alert"
            className={cn(
              'rounded-xl border px-4 py-3 text-sm',
              submitError.severity === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-800',
            )}
          >
            {submitError.message}
          </div>
        )}

        <Separator />
        <p className="text-center text-[10px] text-slate-400" aria-label={`Fuso horário: ${timezoneDisplay}`}>
          {timezoneDisplay}
        </p>
      </div>
    </div>
  );
}
